import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import dayjs from "dayjs";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const UserDashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [movements, setMovements] = useState([]);
  const [saved, setSaved] = useState([]);
  const [rescueData, setRescueData] = useState(null);
  const [poolStatus, setPoolStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading || !user?.firma || !user?.pharmacyCode) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("token");

        // 📊 Dashboard data
        const [statsRes, annRes, movRes, savedRes, rescueRes, poolStatusRes] =
          await Promise.all([
            fetch(`/api/dashboard/${user.firma}/${user.pharmacyCode}/stats`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`/api/dashboard/${user.firma}/announcements`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`/api/dashboard/${user.firma}/${user.pharmacyCode}/movements`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`/api/dashboard/${user.firma}/${user.pharmacyCode}/saved`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`/api/dashboard/${user.firma}/${user.pharmacyCode}/rescue-stats`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`/api/dashboard/${user.firma}/${user.pharmacyCode}/pool-status`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

        const [
          statsData,
          annData,
          movData,
          savedData,
          rescueJson,
          poolStatusJson,
        ] = await Promise.all([
          statsRes.json(),
          annRes.json(),
          movRes.json(),
          savedRes.json(),
          rescueRes.json(),
          poolStatusRes.json(),
        ]);

        setStats([
          { label: "K přijetí", value: statsData.kPrijeti, color: "#22c55e" },
          { label: "K vychystání", value: statsData.kVychystani, color: "#3b82f6" },
          { label: "K odeslání", value: statsData.kOdeslani, color: "#8b5cf6" },
          { label: "Odesláno celkem", value: statsData.odeslanoCelkem, color: "#f97316" },
          { label: "Přijato celkem", value: statsData.prijatoCelkem, color: "#eab308" },
        ]);

        setAnnouncements(annData);
        setMovements(Array.isArray(movData) ? movData : []);
        setSaved(Array.isArray(savedData) ? savedData : []);
        setRescueData(rescueJson);
        setPoolStatus(poolStatusJson);
      } catch (err) {
        console.error("Dashboard error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, authLoading]);

  if (authLoading) return <div className="p-6 text-blue-800">⏳ Načítání uživatele...</div>;
  if (loading) return <div className="p-6 text-blue-800">Načítání dat...</div>;
  if (error) return <div className="p-6 text-red-600">❌ Chyba: {error}</div>;

  // 📈 Přehled pohybů
  const barData = {
    labels: movements.map((m) => dayjs(m.date).format("D.M.")),
    datasets: [
      {
        label: "Odesláno",
        data: movements.map((m) => m.sent),
        backgroundColor: "#f97316",
      },
      {
        label: "Přijato",
        data: movements.map((m) => m.received),
        backgroundColor: "#22c55e",
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: { legend: { position: "bottom" } },
  };

  // 🩺 Podíl zachráněných / likvidace
  const pieData = {
    labels: saved.map((s) => s.name),
    datasets: [
      {
        data: saved.map((s) => s.value),
        backgroundColor: ["#22c55e", "#f43f5e"],
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    plugins: { legend: { position: "bottom" } },
  };

  // 💰 Kumulativní hodnota zachráněných
  const rescueBarData = {
    labels: ["Zachráněné námi", "Zachráněné jinde"],
    datasets: [
      {
        label: "Hodnota (Kč)",
        data: [
          rescueData?.rescuedByUsValue || 0,
          rescueData?.rescuedElsewhereValue || 0,
        ],
        backgroundColor: ["#22c55e", "#3b82f6"],
      },
    ],
  };

  // 📦 Stavové rozložení poolu
  const labelMap = {
    aktivní: "Aktivní",
    rezervováno: "Rezervováno",
    potvrzeno: "Potvrzeno",
    vychystáno: "Vychystáno",
    odesláno: "Odesláno",
    přijato: "Přijato",
    vyřešeno: "Vyřešeno",
    odepsáno: "Odepsáno",
    vyřazeno: "Vyřazeno",
    neznámý: "Neznámý",
  };

  const colorMap = {
    aktivní: "#22c55e",
    rezervováno: "#3b82f6",
    potvrzeno: "#06b6d4",
    vychystáno: "#8b5cf6",
    odesláno: "#f97316",
    přijato: "#eab308",
    vyřešeno: "#16a34a",
    odepsáno: "#f43f5e",
    vyřazeno: "#94a3b8",
    neznámý: "#d1d5db",
  };

  const poolLabels = Object.keys(poolStatus);
  const poolValues = Object.values(poolStatus);
  const poolColors = poolLabels.map((s) => colorMap[s] || "#cccccc");

  const poolStatusData = {
    labels: poolLabels.map((k) => labelMap[k] || k),
    datasets: [
      {
        data: poolValues,
        backgroundColor: poolColors,
      },
    ],
  };

  // 🖼️ Výstup
  return (
    <div className="p-6 text-blue-800 space-y-8">
      {/* 📊 Statistiky */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => (
          <div
            key={s.label}
            className="p-4 bg-white rounded-2xl shadow-md flex flex-col items-center justify-center border"
          >
            <span className="text-gray-500">{s.label}</span>
            <span className="text-3xl font-bold" style={{ color: s.color }}>
              {s.value ?? 0}
            </span>
          </div>
        ))}
      </div>

      {/* 🧾 Nástěnka */}
      <div className="bg-white rounded-2xl shadow-md p-6 border">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">📝 Nástěnka</h3>
        {announcements.length === 0 ? (
          <p className="text-gray-500 italic">Žádné aktuální příspěvky.</p>
        ) : (
          <div className="space-y-4">
            {announcements.map((a, i) => (
              <div key={i} className="border-l-4 border-green-500 pl-4">
                <h4 className="text-gray-800 font-semibold">{a.title}</h4>
                <p className="text-sm text-gray-500 mb-1">
                  {dayjs(a.date).format("D. M. YYYY")} — {a.author || "Systém"}
                </p>
                <p className="text-gray-600 whitespace-pre-line">{a.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 📈 Pohyby + zachráněné */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Přehled pohybů */}
        <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200 p-5 border">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">
            📦 Přehled pohybů (14 dní)
          </h3>
          <div className="h-[180px] w-full">
            <Bar data={barData} options={barOptions} />
          </div>
        </div>

        {/* Zachráněné vs likvidace */}
        <div className="bg-white rounded-2xl shadow-md p-6 border text-center flex flex-col justify-between">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">
            🩺 Podíl zachráněných položek
          </h3>
          <div className="h-[220px] w-full flex items-center justify-center">
            <Pie data={pieData} options={pieOptions} />
          </div>
        </div>
      </div>

      {/* 💰 Kumulativní hodnota + rozložení stavů poolu */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Hodnota zachráněných */}
        <div className="bg-white rounded-2xl shadow-md p-6 border">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">
            💰 Kumulativní hodnota zachráněných položek (Kč)
          </h3>
          <div className="h-[200px] w-full">
            <Bar
              data={rescueBarData}
              options={{
                responsive: true,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true } },
              }}
            />
          </div>
        </div>

        {/* Stavové rozložení poolu */}
        <div className="bg-white rounded-2xl shadow-md p-6 border flex flex-col">
          <h3 className="text-lg font-semibold text-gray-700 mb-3 text-center">
            📦 Aktivní stav položek v poolu
          </h3>
          <div className="flex items-center justify-between">
            <div className="h-[240px] w-[75%]">
              {Object.keys(poolStatus).length > 0 ? (
                <Pie
                  data={poolStatusData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "right",
                        align: "center",
                        labels: { boxWidth: 20, padding: 12 },
                      },
                    },
                  }}
                />
              ) : (
                <p className="text-gray-400 text-center">Žádná data</p>
              )}
            </div>
          </div>
        </div>        
      </div>
    </div>
  );
};

export default UserDashboardPage;
